// src/components/usecases/RealEstateAI_Posting.js
import React from 'react';
import './realestateAI_posting.css';

import estate1 from '../../assets/estate1.png';
import estate2 from '../../assets/estate2.png';
import estate3 from '../../assets/estate3.png';
import estate4 from '../../assets/estate4.png';
import estate5 from '../../assets/estate5.png';

export default function RealEstateAI_Posting() {
  const flowImages = [estate1, estate2, estate3, estate4, estate5];
  return (
    <div className="realestate-page">
      <div className="content-wrapper">
        <h1 className="main-title">
          AI-Powered Personalized Real Estate Report Generator Based on User-Provided Property Data
        </h1>
        <h2 className="sub-title">
          Transforming Detailed Property Inputs into Actionable, Intelligent Real Estate Reports with Conversational AI Assistance
        </h2>

        <p className="intro-text">
          In today’s dynamic and information-rich real estate market, buyers, sellers, and investors alike seek clarity and insight tailored to their unique property situations. Generic listings and templated valuations no longer suffice—what users demand is <strong>personalized, data-driven guidance</strong> that takes their specific property circumstances into account.
        </p>

        {/* UI/UX Flow Images */}
        <div className="flow-wrapper">
          <div className="flow-container">
            {flowImages.map((src, idx) => (
              <img
                key={idx}
                src={src}
                alt={`Real Estate flow ${idx + 1}`}
                className="flow-image"
              />
            ))}
          </div>
        </div>

        <section className="section">
          <h3>🏡 User Input Stage: Rich, Granular Property Data Collection</h3>
          <div className="subsection">
            <h4>Objective</h4>
            <p>Capture deep and specific real estate data from users to serve as the foundation for precise, personalized analysis.</p>
          </div>
          <div className="subsection">
            <h4>Key Features</h4>
            <ul>
              <li><strong>Structured Data Fields:</strong> Property type, location, square footage, rooms, parking, renovation status, zoning, and price expectations.</li>
              <li><strong>Unstructured Descriptions:</strong> Narrative inputs like “has a skyline view” are interpreted via NLP to enrich the dataset.</li>
            </ul>
          </div>
        </section>

        <section className="section">
          <h3>🤖 AI Agent Collaboration: Intelligent Dialog and Recommendation Engine</h3>
          <div className="subsection">
            <h4>Conversational Data Collection</h4>
            <ul>
              <li>AI-guided questions adapt in real time to clarify user inputs (e.g., specifying metro lines or directional exposure).</li>
            </ul>
          </div>
          <div className="subsection">
            <h4>Data Validation and Enrichment</h4>
            <ul>
              <li>Cross-references inputs with cadastral data, transaction records, and zoning maps for accuracy.</li>
            </ul>
          </div>
          <div className="subsection">
            <h4>Preference Customization</h4>
            <ul>
              <li>Users specify report focus (price analysis, rental yield, renovation ROI), which the AI incorporates dynamically.</li>
            </ul>
          </div>
        </section>

        <section className="section">
          <h3>📊 Report Generation: Dynamic, Personalized Real Estate Reports</h3>
          <ul className="features-list">
            <li><strong>Valuation Analysis:</strong> Data-backed estimates from comparables and market trends.</li>
            <li><strong>Rental Yield Forecast:</strong> Calculates expected income and net yield with vacancy and cost factors.</li>
            <li><strong>Market Trends & Projections:</strong> Price appreciation, demographics, and infrastructure insights.</li>
            <li><strong>Investment Score & Risk Assessment:</strong> Liquidity, volatility, and regulatory risk modeling.</li>
            <li><strong>Smart Recommendations:</strong> Listing price, timing, and renovation suggestions tailored to user goals.</li>
          </ul>
        </section>

        <section className="section">
          <h3>📈 Ongoing Learning and Adaptation</h3>
          <p>
            The AI agent continuously learns from user submissions, feedback, and market data, refining future reports to be more accurate and insightful.
          </p>
        </section>

        <section className="section conclusion">
          <h3>Conclusion</h3>
          <p>
            By combining conversational data collection, robust validation, and dynamic report generation, this platform delivers a personal real estate advisor—making analysis smarter, more accessible, and uniquely tailored to each user.
          </p>
        </section>
      </div>
    </div>
  );
}